import { ChevronDown, ChevronRight, Lock } from 'lucide-react'
import clsx from 'clsx'
import { ConfidenceSlider } from '@/components/ConfidenceSlider'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Tooltip } from '@/components/Tooltip'
import { fromPercent, toPercent } from '@/lib/confidence'
import { IDENTIFIER_RULE_MESSAGE } from '@/lib/identifierValidation'
import type { OutputResolutionEntry } from '@/types/bundle'
import { PriorityList } from './PriorityList'

export interface OutputResolutionRowProps {
  entryKey: string
  entry: OutputResolutionEntry
  isLocked: boolean
  /** Only meaningful while !isLocked - the in-progress typed field name. */
  draftName: string
  onNameChange: (name: string) => void
  onChange: (entry: OutputResolutionEntry) => void
  onDelete: () => void
  onDuplicate: () => void
  expanded: boolean
  onToggleExpanded: () => void
  /** This row's resolved name collides with another entry's - Save is already
   * blocked while true, this only drives the inline message. */
  isDuplicateName: boolean
  /** This row's resolved name isn't snake_case (PRD 34) - same "Save already
   * blocked, this only drives the inline message" role as isDuplicateName. */
  isInvalidName: boolean
  documentFieldOptions: string[]
  outputFieldOptions: string[]
  serverErrors?: Record<string, string>
  isNew?: boolean
}

// Structural mirror of document-types' FieldRow.tsx - same collapsed-header/
// expand-to-edit layout, same Duplicate/Delete text-link actions, same
// locked-until-named identifier convention - per the admin's own request
// that this "look like document types fields."
export function OutputResolutionRow({
  entryKey,
  entry,
  isLocked,
  draftName,
  onNameChange,
  onChange,
  onDelete,
  onDuplicate,
  expanded,
  onToggleExpanded,
  isDuplicateName,
  isInvalidName,
  documentFieldOptions,
  outputFieldOptions,
  serverErrors,
  isNew,
}: OutputResolutionRowProps) {
  const displayName = isLocked ? entryKey : draftName.trim() || 'New output field'

  return (
    <div
      className={clsx(
        'flex flex-col gap-4 rounded-control border border-border bg-surface p-4',
        isNew && 'animate-[field-row-enter_200ms_ease-out]',
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onToggleExpanded}
          className="flex flex-1 items-center gap-2 text-left text-body font-medium text-text-primary"
        >
          {expanded ? (
            <ChevronDown size={16} strokeWidth={1.5} className="text-text-secondary" />
          ) : (
            <ChevronRight size={16} strokeWidth={1.5} className="text-text-secondary" />
          )}
          {displayName}
          <span className="text-meta text-text-secondary">{toPercent(entry.min_confidence)}%</span>
        </button>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onDuplicate} className="text-meta font-medium text-accent hover:underline">
            Duplicate
          </button>
          <button type="button" onClick={onDelete} className="text-meta font-medium text-danger hover:underline">
            Delete
          </button>
        </div>
      </div>

      {expanded && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-2 text-meta font-medium text-text-secondary">
              Output field name
              {isLocked && (
                <Tooltip text="Can't change after creation. Add a new output field if you need a different name.">
                  <span tabIndex={0} className="text-text-disabled">
                    <Lock size={12} strokeWidth={1.5} />
                  </span>
                </Tooltip>
              )}
            </span>
            <input
              type="text"
              value={isLocked ? entryKey : draftName}
              disabled={isLocked}
              onChange={(event) => onNameChange(event.target.value)}
              className="w-64 rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none disabled:cursor-not-allowed disabled:text-text-disabled"
            />
            {serverErrors?.name && <span className="text-meta text-danger">{serverErrors.name}</span>}
            {!isLocked && draftName.trim().length === 0 && (
              <span className="text-meta text-danger">Output field name is required</span>
            )}
            {isDuplicateName && (
              <span className="text-meta text-danger">
                Another output field already uses this name - names must be unique
              </span>
            )}
            {isInvalidName && <span className="text-meta text-danger">{IDENTIFIER_RULE_MESSAGE}</span>}
          </div>

          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
              Priority
              <InfoTooltip text="Tries each one in order, top to bottom, and uses the first value it finds." />
            </span>
            <PriorityList
              value={entry.priority}
              onChange={(priority) => onChange({ ...entry, priority })}
              documentFieldOptions={documentFieldOptions}
              outputFieldOptions={outputFieldOptions}
            />
            {entry.priority.length === 0 && (
              <span className="text-meta text-danger">At least one priority is required</span>
            )}
            {serverErrors?.priority && <span className="text-meta text-danger">{serverErrors.priority}</span>}
          </div>

          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
              Min confidence
              <InfoTooltip text="How sure the system must be before using this value from the list above." />
            </span>
            <ConfidenceSlider
              value={toPercent(entry.min_confidence)}
              onChange={(percent) => onChange({ ...entry, min_confidence: fromPercent(percent) })}
            />
            {serverErrors?.min_confidence && (
              <span className="text-meta text-danger">{serverErrors.min_confidence}</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
